import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import {
  ContactPhoneOutlined as ContactIcon,
  Inventory2Outlined as MaterialIcon,
  LocationOnOutlined as LocationIcon,
  NotesOutlined as NotesIcon,
  PhoneOutlined as PhoneIcon,
  ScheduleOutlined as HoursIcon,
} from '@mui/icons-material';
import DialogHeader from '../common/DialogHeader';
import LabelChips from '../common/LabelChips';
import { APP_SHAPE } from '../../constants/shape';
import { formatOperatingHours } from '../../utils/operatingHours';
import type { SupplierCollectionPoint } from '../../types/supplier.type';

/** Mirrors the icon+label+content row already used by `CollectionPointsMap.tsx`'s own detail panel. */
function Detail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

export interface CollectionPointDetailsDialogProps {
  open: boolean;
  point: SupplierCollectionPoint | null;
  supplierName?: string;
  materialLabelsById: ReadonlyMap<string, string>;
  onClose: () => void;
}

/** Read-only "view" for a collection point -- no such dialog existed before; every other
 * collection-point component in this app is either an edit form or a map popup. Content and
 * locale keys mirror `CollectionPointsMap.tsx`'s own detail panel for consistency. */
export default function CollectionPointDetailsDialog({
  open,
  point,
  supplierName,
  materialLabelsById,
  onClose,
}: CollectionPointDetailsDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: `${APP_SHAPE.radiusXl}px` } }}>
      {point ? (
        <>
          <DialogHeader title={point.name} subtitle={supplierName} />
          <DialogContent sx={{ p: 2.5 }}>
            <Stack spacing={2.5}>
              <Detail icon={<LocationIcon />} label={t('suppliers.collectionPoints.detail.address')}>
                {point.address || `${point.latitude}, ${point.longitude}`}
              </Detail>
              {point.contact_name ? (
                <Detail icon={<ContactIcon />} label={t('suppliers.collectionPoints.detail.contact')}>
                  {point.contact_name}
                </Detail>
              ) : null}
              {point.contact_phone ? (
                <Detail icon={<PhoneIcon />} label={t('suppliers.collectionPoints.detail.phone')}>
                  {point.contact_phone}
                </Detail>
              ) : null}
              {point.operating_hours && (point.operating_hours.days.length || point.operating_hours.start_time) ? (
                <Detail icon={<HoursIcon />} label={t('suppliers.collectionPoints.detail.operatingHours')}>
                  {formatOperatingHours(point.operating_hours, (day) => t(`collectionSites.days.${day}`))}
                </Detail>
              ) : null}
              {point.labels && point.labels.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {t('suppliers.collectionPoints.table.labels')}
                  </Typography>
                  <LabelChips labels={point.labels} />
                </Box>
              ) : null}
              {point.material_ids.length > 0 ? (
                <Box>
                  <Detail icon={<MaterialIcon />} label={t('suppliers.collectionPoints.detail.wasteTypes')}>
                    {''}
                  </Detail>
                  <Stack direction="row" gap={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {point.material_ids.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={materialLabelsById.get(id) ?? id}
                        sx={{ borderRadius: `${APP_SHAPE.radiusSm}px` }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {point.notes ? (
                <Detail icon={<NotesIcon />} label={t('suppliers.collectionPoints.detail.notes')}>
                  {point.notes}
                </Detail>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={onClose}>{t('suppliers.collectionPoints.detail.close')}</Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
